import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraTimeline } from '@mono/ui-components/captains';
import { SpecraAssistAttribution, SpecraBadge, SpecraButton, SpecraIcon, SpecraInput, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraPopover } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { Lock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { relative, timestampToDate, versionSourceSuffix } from './statusText';
import { useDocumentVersions } from './useOfficeWorkspace';
/**
 * The History tab (F12): every save is a version; name the current one, open
 * any of them read-only, restore from the view banner. Shared with PRP-22.
 */ export function VersionHistoryRail({ canEdit, container, currentVersionId, documentId, onCompareWithCurrent, onNameVersion, onSelectVersion, selectedVersionId, userName }) {
    const versions = useDocumentVersions(container, documentId);
    const [anchor, setAnchor] = useState(null);
    const [open, setOpen] = useState(false);
    const [label, setLabel] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    const items = useMemo(()=>(versions.data?.pages ?? []).flatMap((page)=>page.versions.map((v, i)=>{
                const attribution = page.attributions?.[i];
                const who = v.createdByUserId ? userName(v.createdByUserId) : '';
                const when = relative(timestampToDate(v.createdAt));
                const viaAssist = attribution?.via === 'specra_assist';
                return {
                    id: v.id,
                    title: `v${v.versionNumber}`,
                    description: viaAssist ? /*#__PURE__*/ _jsx(SpecraAssistAttribution, {
                        approver: who || attribution?.actorDisplayName || 'Someone',
                        at: when,
                        via: "specra_assist"
                    }) : [
                        when,
                        who
                    ].filter(Boolean).join(' · ') + versionSourceSuffix(v.source),
                    badge: v.label ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "neutral",
                        children: [
                            v.locked ? /*#__PURE__*/ _jsx(SpecraIcon, {
                                "aria-hidden": "true",
                                className: "mr-[var(--space-1)]",
                                size: "sm",
                                children: /*#__PURE__*/ _jsx(Lock, {})
                            }) : null,
                            v.label
                        ]
                    }) : undefined,
                    actions: onCompareWithCurrent && v.id !== currentVersionId ? /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>onCompareWithCurrent(v.id),
                        size: "sm",
                        variant: "link",
                        children: "Compare with current"
                    }) : undefined
                };
            })), [
        versions.data,
        userName,
        onCompareWithCurrent,
        currentVersionId
    ]);
    const submit = async (e)=>{
        e.preventDefault();
        if (!label.trim() || pending) return;
        setPending(true);
        setError(null);
        try {
            await onNameVersion(label.trim());
            setOpen(false);
            setLabel('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'The version could not be named.');
        } finally{
            setPending(false);
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-3)] p-[var(--space-4)]",
        "data-testid": Testids.officeWorkspace.historyPanel,
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "Every save is a version. Name the ones that matter; open or restore any of them."
            }),
            canEdit && currentVersionId ? /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.officeWorkspace.nameVersion,
                        onClick: ()=>setOpen((o)=>!o),
                        ref: setAnchor,
                        size: "sm",
                        variant: "secondary",
                        children: "Name this version"
                    }),
                    /*#__PURE__*/ _jsx(SpecraPopover, {
                        anchor: anchor,
                        "aria-label": "Name this version",
                        onOpenChange: setOpen,
                        open: open,
                        children: /*#__PURE__*/ _jsxs("form", {
                            className: "flex w-64 flex-col gap-[var(--space-2)]",
                            onSubmit: submit,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Version name",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        autoFocus: true,
                                        "data-testid": Testids.officeWorkspace.nameVersionInput,
                                        id: "office-name-version",
                                        onChange: (e)=>setLabel(e.target.value),
                                        placeholder: "e.g. Sent to review",
                                        value: label
                                    })
                                }),
                                error ? /*#__PURE__*/ _jsx(SpecraText, {
                                    role: "alert",
                                    size: "caption",
                                    tone: "danger",
                                    children: error
                                }) : null,
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex justify-end",
                                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": Testids.officeWorkspace.nameVersionSubmit,
                                        disabled: !label.trim() || pending,
                                        isLoading: pending,
                                        size: "sm",
                                        type: "submit",
                                        children: "Save name"
                                    })
                                })
                            ]
                        })
                    })
                ]
            }) : null,
            versions.isLoading ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                label: "Loading versions"
            }) : versions.isError ? /*#__PURE__*/ _jsx(SpecraText, {
                role: "alert",
                size: "caption",
                tone: "danger",
                children: "The version history didn't load."
            }) : /*#__PURE__*/ _jsx(SpecraTimeline, {
                emptyText: "No saved versions yet.",
                getItemTestId: ()=>Testids.officeWorkspace.historyItem,
                items: items,
                onItemSelect: onSelectVersion,
                selectedId: selectedVersionId ?? currentVersionId
            }),
            versions.hasNextPage ? /*#__PURE__*/ _jsx(SpecraButton, {
                disabled: versions.isFetchingNextPage,
                onClick: ()=>void versions.fetchNextPage(),
                size: "sm",
                variant: "link",
                children: "Load older"
            }) : null
        ]
    });
}
